import React, { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  List,
  Box,
  Collapse,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getSchema } from "../helpers/schemas/getSchema";
import { getFormInvalid } from "../helpers/getFormInvalid";
import { checkInvalidField } from "../helpers/checkInvalidField";
import ImportDialog from "../Dialogs/ImportDialog";
import EditDialog from "../Dialogs/EditDialog";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import IssuesCard from "./IssuesCard";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { getExcelFeatures } from "../helpers/getExcelFeatures";
import SortDialog from "../Dialogs/SortDialog";
import { getSortArray } from "../helpers/getSortArray";
import setupEdit from "../helpers/setupEdit";
import { getCounts } from "../helpers/getCounts";
import { processFiles } from "../helpers/processFiles";
import { onEditSaveAll } from "../helpers/onEditSaveAll";
import { onEditDeleteAll } from "../helpers/onEditDeleteAll";
import { VisuallyHiddenInput } from "../helpers/VisuallyHiddenInput";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { getUserAccess } from "../helpers/getUserAccess";
// import { saveToFirestore } from "../helpers/saveToFirestore";
import { Unpublished } from "@mui/icons-material";
import { getEditorTrackingFieldNames } from "../helpers/schemas/getEditorTrackingFields";

export default function IssuesList(props) {
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);
  const navigate = useNavigate();
  // console.log(location.state);
  const { enqueueSnackbar } = useSnackbar();
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const [project, setProject] = React.useState({});
  const [allIssues, setAllIssues] = React.useState([]);
  const [issues, setIssues] = React.useState([]);
  const [sortByValue, setSortByValue] = React.useState("No");
  const [sortByOptions] = React.useState([
    "No",
    "No Desc",
    "Status",
    "Status Desc",
    "Priority",
    "Priority Desc",
    "Discipline",
    "Discipline Desc",
  ]);
  const [importTitle, setImportTitle] = React.useState(
    "Issues Import > Choose Worksheet",
  );
  const [importStats, setImportStats] = React.useState([]);
  const [importSubtitle, setImportSubtitle] = React.useState(
    `Choose the worksheet with the data you wish to import.`,
  );
  const [importData, setImportData] = React.useState([]);
  const [importFields, setImportFields] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterValue, setFilterValue] = React.useState("");
  const [editFeature, setEditFeature] = React.useState(null);
  const [editFiles, setEditFiles] = React.useState([]);
  const [editSchema, setEditSchema] = React.useState([]);
  const [formErrorMessage, setformErrorMessage] = React.useState("");
  const [sheetName, setSheetName] = React.useState(null);
  const [workbook, setWorkBook] = React.useState({ SheetNames: [] });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);

  useEffect(() => {
    const fetchData = () => {
      var isus = props.project.Issues;
      isus = getCounts(
        isus,
        props.project.Files,
        [],
        props.project.Comments,
        "issue_id",
      );
      setAllTheIssues(isus);
      setProject(props.project);
      setEditSchema(getSchema("issue"));
      setIsLoading(false);
    };
    if (editSchema.length === 0 && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const setAllTheIssues = (iss) => {
    var reslts;
    if (props.observationId !== null) {
      reslts = iss.filter((i) => i.observation_id === props.observationId);
    } else if (props.equipmentId !== null) {
      reslts = iss.filter((i) => i.equipment_id === props.equipmentId);
    } else {
      reslts = iss;
    }
    setAllIssues([...reslts]);
    onSortChange(sortByValue, [...reslts]);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setIssues([...obs]);
  };

  const getAcceptedFiles = async (files) => {
    try {
      const readyFiles = await processFiles(files, "issue_id", editFeature.id);
      const allFiles = editFiles.concat(readyFiles);
      setEditFiles([...allFiles]);
    } catch (error) {
      console.error("Error getting document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        variant: "error",
      });
    }
  };

  const onEditFileCommentChange = (e) => {
    var file = editFiles.find((f) => f.id === e.target.id);
    file.comment = e.target.value;
    setEditFiles([...editFiles]);
  };

  const onDrawComplete = (id, pic) => {
    var file = editFiles.find((f) => f.id === id);
    if (file) {
      file.file = pic;
      if (!file.id.startsWith("new_")) {
        file.isDrawn = true;
      }
      setEditFiles([...editFiles]);
    }
  };

  const onRemoveEditFile = async (id) => {
    const updatedFiles = editFiles.map((obj) => {
      if (obj.id === id) {
        obj.deleted = true;
        return obj; // Replace the object if its ID matches
      }
      return obj; // Keep other objects as they are
    });

    setEditFiles([...updatedFiles]);
  };

  const onAddIssue = () => {
    var item = {};
    var schema = getSchema("issue");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["id"] = "new";
    item["project_id"] = project["id"];
    if (props.observationId !== null) {
      item["observation_id"] = props.observationId;
    } else if (props.equipmentId !== null) {
      item["equipment_id"] = props.equipmentId;
    }
    item = applyEditTrackingFields(item, props.user);

    setEditSchema(schema);
    setEditFeature(item);
    setEditFiles([]);
    setImportTitle("New Issue");
    setEditDialogOpen(true);
  };

  const onEdit = (id) => {
    var item = issues.find((f) => f.id === id);
    item = { ...item };
    const results = setupEdit(item, "issue");
    const files = project.Files.filter((p) => p.issue_id === id);
    setEditSchema(results.schema);
    setEditFeature({ ...results.item });
    setEditFiles([...files]);
    setImportTitle(`Edit Issue (No:${item.No})`);
    setEditDialogOpen(true);
  };

  const onEditDelete = async () => {
    try {
      setIsLoading(true);
      const savedProj = await onEditDeleteAll(
        project,
        editFeature,
        editFiles,
        "Issues",
        "issues_id",
        props.user,
      );

      // props.saveCurrentProject({ ...savedProj });
      setProject({ ...savedProj });
      setAllTheIssues(savedProj.Issues);
      enqueueSnackbar("Issue deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setEditDialogOpen(false);
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditSave = async () => {
    try {
      //check for invalid
      var feat = { ...editFeature };
      var invalidResults = getFormInvalid(feat, editSchema);
      if (invalidResults.isInvalid === true) {
        setEditFeature({ ...invalidResults.feature });
        setEditSchema([...invalidResults.schema]);
        setformErrorMessage(invalidResults.formErrorMessage);
        return;
      }

      setIsLoading(true);

      const savedProj = await onEditSaveAll(
        project,
        feat,
        editSchema,
        editFiles,
        "Issues",
        "issue_id",
        props.user,
      );

      // props.passUpProject(savedProj);
      // props.passIsLoading(false);
      // props.saveCurrentProject({ ...savedProj });
      setProject({ ...savedProj });
      setAllTheIssues(savedProj.Issues);
      setEditDialogOpen(false);
      enqueueSnackbar("Issue saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditChange = (e) => {
    try {
      // console.log(e);
      var att = editSchema.find((item) => item.name === e.target.name);
      //clearout erro message field
      const formErrorMessage = checkInvalidField(e.target.value, att);
      //format dates
      if (att.type === "date") {
        if (e.target.value.length > 0) {
          var date = new Date(e.target.value);
          // date.setDate(date.getDate() + 1);
          editFeature[e.target.name] = date.toISOString().split("T")[0];
          // editFeature[e.target.name] = date.getTime();
          setEditFeature({ ...editFeature });
          setformErrorMessage(formErrorMessage);
        }
      } else if (att.type === "bit") {
        editFeature[e.target.name] = e.target.checked;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else {
        editFeature[e.target.name] = e.target.value;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditCancel = () => {
    setformErrorMessage("");
    setEditDialogOpen(false);
  };

  const onDetailsClick = (item) => {
    if (item.focus.length > 0) {
      navigate(`/project/${project.id}/issue/${item.id}?focus=${item.focus}`, {
        state: { passProject: project },
      });
    } else {
      navigate(`/project/${project.id}/issue/${item.id}`, {
        state: { passProject: project },
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allIssues.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });
      setIssues([...filteredData]);
    } else {
      setIssues([...allIssues]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChecked = (e) => {
    const item = issues.find((item) => item.id === e.target.id);
    item.Selected = !item.Selected;
    setIssues([...issues]);
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (fileInputRef1.current) {
      fileInputRef1.current.value = null;
    }
    setSheetName(null);
    setImportTitle("Issues Import > Choose Worksheet");
    setImportSubtitle(`Choose the worksheet with the data you wish to import.`);
  };

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const processExcel = (file) => {
    try {
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          setWorkBook(workbook);
          setImportDialogOpen(true);
          setImportTitle("Choose a worksheet");
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onWorksheetSelected = async (e) => {
    try {
      // Assuming you want to read the first sheet
      const worksheet = workbook.Sheets[e];

      // Convert sheet data to an array of objects or an array of arrays
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Array of objects
      // const arrayOfArrays = XLSX.utils.sheet_to_array(worksheet); // Array of arrays

      console.log(jsonData); // Or update your component state with this data
      if (jsonData.length > 0) {
        var fields = [];
        Object.keys(jsonData[0]).forEach((colName) => {
          var match = editSchema.find((f) => f.name === colName);
          if (match === undefined) {
            editSchema.forEach((fd) => {
              if (
                fd.hasOwnProperty("similair") &&
                fd.similair.includes(colName.toLowerCase())
              )
                match = fd;
            });
          }

          var availableFds = editSchema
            .filter(
              (f) =>
                f.name !== "id" &&
                !f.name.includes("_id") &&
                getEditorTrackingFieldNames().includes(f.name) === false &&
                f.name !== "fileCount" &&
                f.name !== "commentCount" &&
                f.name !== "No",
            )
            .map((f) => f.name);

          availableFds.push("New Attribute");

          fields.push({
            name: colName,
            selected: match ? match.name : "New Attribute",
            options: availableFds,
            attribute: colName,
            error: false,
          });
        });
        setImportData(jsonData);
        setSheetName(e);
        setImportFields(fields);
        setImportTitle("Issues Import > Choose Fields");
        setImportSubtitle(
          `For each column of your spreadsheet, select the corresponding field. The "Issue Description" field is required.`,
        );
      } else {
        throw new Error("Unable to import issues");
      }
    } catch (err) {
      throw new Error("Unable to import issues: " + err.message);
    }
  };

  const onImportExcel = async () => {
    try {
      if (importData.length === 0) {
        onCloseImportDialog();
        return;
      }

      const results = await getExcelFeatures(
        props.user,
        importData,
        importFields,
        editSchema,
        ["Issue Description"],
        // getIssueSchema()
        //   .filter(
        //     (fd) =>
        //       fd.name !== "id" &&
        //       fd.name !== "project_id" &&
        //       fd.nullable === false
        //   )
        //   .map((fd) => fd.name),
        project,
        "Issues",
        props.observationId !== null
          ? "observation_id"
          : props.equipmentId !== null
            ? "equipment_id"
            : null,
        props.observationId !== null
          ? props.observationId
          : props.equipmentId !== null
            ? props.equipmentId
            : null,
      );

      setImportStats([...getStats(results.newEquip)]);
      setImportSubtitle("Import Successful: Results shown below");
      setImportTitle("Issues Import > Complete");
      setImportFields([]);
      setFilterValue("");
      setPage(0);
      setProject({ ...results.project });
      setAllTheIssues(results.freshFeatures);
      setImportData([]);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      // onCloseImportDialog();
    }
  };

  const getStats = (array) => {
    var importStat = [];
    importStat.push({ name: "Issues", count: array.length });
    return importStat;
  };

  const onDownloadIssues = () => {
    try {
      const dataToExport = issues.map(({ Selected, ...rest }) => rest);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Issues");
      XLSX.writeFile(workbook, `${project.Name}_Issues.xlsx`);
    } catch (error) {
      console.error("Error exporting Issues:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onUncheckSelected = () => {
    issues.forEach((item) => {
      if (item.Selected === true) {
        item.Selected = false;
      }
    });
    setIssues([...issues]);
  };

  // const onDeleteSelected = async () => {
  //   try {
  //     setIsLoading(true);

  //     const deleteIds = issues
  //       .filter((item) => item.Selected === true)
  //       .map((i) => i.id);

  //     //set remaining issues
  //     project.Issues = project.Issues.filter(
  //       (item) => !deleteIds.includes(item.id),
  //     );

  //     //set remaining Checklists, Files, Issues, Comments

  //     project.Files = project.Files.filter(
  //       (item) =>
  //         item.hasOwnProperty("issue_id") === false ||
  //         (item.hasOwnProperty("issue_id") &&
  //           !deleteIds.includes(item.issue_id)),
  //     );

  //     project.Comments = project.Comments.filter(
  //       (item) =>
  //         item.hasOwnProperty("issue_id") === false ||
  //         (item.hasOwnProperty("issue_id") &&
  //           !deleteIds.includes(item.issue_id)),
  //     );
  //     const savedProj = { ...project };

  //     await saveToFirestore("projects", savedProj);
  //     setProject({ ...savedProj });
  //     setAllIssues([...savedProj.Issues]);
  //     onSortChange(sortByValue, savedProj.Issues);
  //     enqueueSnackbar("Selected issues deleted successfully", {
  //       anchorOrigin: {
  //         vertical: "bottom",
  //         horizontal: "center",
  //       },
  //       variant: "success",
  //     });
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.error("Error:", error);
  //     enqueueSnackbar(error.message, {
  //       anchorOrigin: {
  //         vertical: "bottom",
  //         horizontal: "center",
  //       },
  //       variant: "error",
  //     });
  //     setIsLoading(false);
  //   }
  // };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div>
      <Stack
        // style={{ paddingTop: 10 }}
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Tooltip title="Upload Issues">
          <IconButton
            color="primary"
            component="label"
            disabled={
              !getUserAccess(
                props.user,
                props.project.uid,
                props.project.Contacts,
              ).allowCreate
            }
            role={undefined}
            variant="contained"
            tabIndex={-1}
          >
            <VisuallyHiddenInput
              type="file"
              ref={fileInputRef}
              onChange={(event) =>
                event.target.files.length > 0 &&
                processExcel(event.target.files[0])
              }
            />
            <FileUploadIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Issue">
          <IconButton
            onClick={onAddIssue}
            disabled={
              !getUserAccess(
                props.user,
                props.project.uid,
                props.project.Contacts,
              ).allowCreate
            }
            aria-label="Add Issue"
            size="large"
            color="primary"
          >
            <AddBoxIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download Issues">
          <IconButton
            onClick={onDownloadIssues}
            aria-label="Download Issues"
            disabled={issues.length === 0}
            size="large"
            color="primary"
          >
            <CloudDownloadIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        {/* <ExportButton
          issues={issues}
          project={project}
          exportTableName="Issues"
        /> */}
      </Stack>

      <Stack
        style={{ paddingTop: 30 }}
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {allIssues.length > 0 && (
          <SortDialog
            sortByValue={sortByValue}
            onSortChange={onSortChange}
            objArray={issues}
            sortByOptions={sortByOptions}
            filterValue={filterValue}
            onFilterChange={onFilterChange}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}

        <Collapse
          style={{ width: "100%" }}
          in={issues.some((item) => item.Selected === true)}
        >
          <Paper elevation={6} style={{ width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                height: 60,
                borderRadius: 1,
                bgcolor: "secondary.light",
                // "&:hover": {
                //   bgcolor: "primary.dark",
                // },
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
                style={{ height: "100%", paddingLeft: 16 }}
              >
                <Typography color="primary.contrastText">
                  {`Selected ${
                    issues.filter((f) => f.Selected === true).length
                  }`}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUncheckSelected}
                  startIcon={<Unpublished />}
                >
                  Uncheck All
                </Button>

                {/* <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !getUserAccess(props.user, project.uid, project.Contacts)
                      .allowDelete
                  }
                  startIcon={<Delete />}
                  onClick={onDeleteSelected}
                >
                  {`Delete Selected`}
                </Button> */}
              </Stack>
            </Box>
          </Paper>
        </Collapse>

        {issues.length === 0 && filterValue.length <= 2 ? (
          <Typography>
            There aren't any issues associated with this
            {props.equipmentId
              ? " equipment"
              : props.issueId
                ? " issue"
                : props.observationId
                  ? " observation"
                  : "project"}
            . Please
            <Tooltip title="Upload Issues">
              <IconButton
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                color="primary"
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                size="medium"
              >
                <VisuallyHiddenInput
                  type="file"
                  ref={fileInputRef1}
                  onChange={(event) =>
                    event.target.files.length > 0 &&
                    processExcel(event.target.files[0])
                  }
                />
                <FileUploadIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            upload or add
            <Tooltip title="Add Issue">
              <IconButton
                onClick={onAddIssue}
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                aria-label="Add Issue"
                size="medium"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            new Issue
          </Typography>
        ) : issues.length === 0 && filterValue.length > 2 ? (
          <Typography>
            There aren't any issues associated with your search: "
            <strong>{filterValue}</strong>"
          </Typography>
        ) : (
          <List style={{ width: "100%" }}>
            <IssuesCard
              issues={issues}
              page={page}
              rowsPerPage={rowsPerPage}
              onEdit={onEdit}
              allowUpdate={
                getUserAccess(props.user, project.uid, project.Contacts)
                  .allowUpdate
              }
              projectID={project.id}
              onDetailsClick={onDetailsClick}
              handleChecked={handleChecked}
            />
          </List>
        )}
      </Stack>
      <ImportDialog
        isOpen={importDialogOpen}
        onClose={onCloseImportDialog}
        importTitle={importTitle}
        importSubtitle={importSubtitle}
        importData={importData}
        importFields={importFields}
        onFldChange={onFldChange}
        onAttributeChange={onAttributeChange}
        onImportExcel={onImportExcel}
        importStats={importStats}
        sheetName={sheetName}
        workbook={workbook}
        onWorksheetSelected={onWorksheetSelected}
      />

      <EditDialog
        allowDeleteAccess={
          getUserAccess(props.user, project.uid, project.Contacts).allowDelete
        }
        feature={editFeature}
        editFiles={editFiles}
        importTitle={importTitle}
        getAcceptedFiles={getAcceptedFiles}
        onEditFileCommentChange={onEditFileCommentChange}
        onRemoveEditFile={onRemoveEditFile}
        schema={editSchema}
        onChange={onEditChange}
        onEditSave={onEditSave}
        onEditDelete={onEditDelete}
        isOpen={editDialogOpen}
        onClose={onEditCancel}
        formErrorMessage={formErrorMessage}
        allowDelete={true}
        allowFiles={true}
        onDrawComplete={onDrawComplete}
      />
    </div>
  );
}
